import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useSnackbarController } from "@rebasepro/app";
import { Alert, Button, Card, cardClickableMixin, cardSelectedMixin, Chip, CircularProgressCenter, cls, iconSize, LucideIconByName, Separator, Typography } from "@rebasepro/ui";
import { inboxPath } from "rebase-leadgen-config";
import { client } from "../lib/rebaseClient";
import { EmailComposer } from "../inbox/EmailComposer";
import { EmailThreadView } from "../inbox/EmailThreadView";
import {
    daysWaiting,
    describeError,
    sinceLabel,
    threadTitle,
    threadWith,
    useMailboxes,
    type Message,
    type Thread
} from "../inbox/shared";

/**
 * The mail with one person, or one company — read, answered and started from
 * their own page.
 *
 * A person's conversations are the ones linked to them AND the ones they are
 * simply on: a conversation the sync could not link is still theirs if their
 * address is in it, and hiding it here because nobody pressed "link" would be
 * hiding their mail from their own page.
 */

export function EntityEmailPanel({
    kind,
    entityId,
    email,
    name,
    companyName,
    limit,
    showCompose = true
}: {
    kind: "person" | "company";
    entityId: string;
    email?: string | null;
    name?: string | null;
    companyName?: string | null;
    /** Show only this many, with a note — for the overview. */
    limit?: number;
    showCompose?: boolean;
}) {
    const navigate = useNavigate();
    const snackbar = useSnackbarController();
    const { mailboxes } = useMailboxes();
    const [threads, setThreads] = useState<Thread[] | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [composing, setComposing] = useState(false);
    const [replying, setReplying] = useState(false);
    const [error, setError] = useState<string | undefined>();

    const load = useCallback(async () => {
        try {
            const queries: Record<string, unknown>[] = kind === "person"
                ? [{ prospectId: ["==", entityId] }, ...(email ? [{ participants: ["array-contains", email.toLowerCase()] }] : [])]
                : [{ companyId: ["==", entityId] }];
            const found = new Map<string, Thread>();
            for (const where of queries) {
                const { data } = await client.data.collection<Thread>("email_threads").find({ where: where as never, orderBy: ["last_message_at", "desc"], limit: 50 });
                for (const thread of data ?? []) found.set(thread.id, thread);
            }
            setThreads([...found.values()].sort((a, b) => String(b.last_message_at ?? "").localeCompare(String(a.last_message_at ?? ""))));
        } catch (err) {
            setError(describeError(err));
            setThreads([]);
        }
    }, [kind, entityId, email]);

    useEffect(() => { void load(); }, [load]);

    const loadMessages = useCallback(async (threadId: string) => {
        const { data } = await client.data.collection<Message>("email_messages").find({ where: { threadId: ["==", threadId] } as never, orderBy: ["sent_at", "asc"], limit: 200 });
        setMessages(data ?? []);
    }, []);

    useEffect(() => {
        setMessages([]);
        setReplying(false);
        if (selectedId) void loadMessages(selectedId).catch(err => setError(describeError(err)));
    }, [selectedId, loadMessages]);

    const selected = threads?.find(t => t.id === selectedId) ?? null;
    const shown = limit ? (threads ?? []).slice(0, limit) : threads ?? [];
    const label = name || (kind === "person" ? "them" : "this company");

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
                <Typography variant="subtitle2" className="grow">
                    Email with {label}{threads?.length ? ` (${threads.length})` : ""}
                </Typography>
                {showCompose && (
                    <Button size="small" variant={composing ? "text" : "outlined"} onClick={() => { setComposing(v => !v); setSelectedId(null); }}>
                        {composing ? "Cancel" : <><LucideIconByName name="PenLine" size={iconSize.smallest} /> Write</>}
                    </Button>
                )}
            </div>

            {showCompose && composing && kind === "person" && !email && (
                <Alert color="info" size="small">There is no address on file for {label}. Type one in To, and add it to their record so the next message knows it.</Alert>
            )}

            {composing && (
                <EmailComposer
                    compact
                    target={{
                        mode: "new",
                        to: email ? [email] : [],
                        subject: "",
                        prospectId: kind === "person" ? entityId : null,
                        companyId: kind === "company" ? entityId : null,
                        recipientName: kind === "person" ? name : null,
                        companyName: kind === "company" ? name : companyName
                    }}
                    mailboxes={mailboxes}
                    onCancel={() => setComposing(false)}
                    onSent={async ({ threadId }) => {
                        setComposing(false);
                        snackbar.open({ type: "success", message: "Sent" });
                        await load();
                        setSelectedId(threadId);
                    }}
                />
            )}

            {error && <Alert color="error" size="small">{error}</Alert>}
            {threads === null && <CircularProgressCenter />}
            {threads?.length === 0 && !composing && (
                <Typography variant="body2" color="secondary">
                    No email with {label} yet. What is sent from here — and what they send back — shows up on this page.
                </Typography>
            )}

            <div className="flex flex-col gap-1.5">
                {shown.map(thread => {
                    const waiting = daysWaiting(thread);
                    return (
                        <Card
                            key={thread.id}
                            className={cls("p-3 cursor-pointer", cardClickableMixin, thread.id === selectedId && cardSelectedMixin)}
                            onClick={() => setSelectedId(id => (id === thread.id ? null : thread.id))}
                        >
                            <div className="flex items-baseline gap-2">
                                <Typography variant="body2" className={cls("truncate grow", thread.unread ? "font-bold" : "font-medium")}>{threadTitle(thread)}</Typography>
                                <Typography variant="caption" color="secondary" className="shrink-0">{sinceLabel(thread.last_message_at)}</Typography>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap mt-0.5">
                                <Typography variant="caption" color="secondary">{threadWith(thread)} · {thread.message_count ?? 0} message{thread.message_count === 1 ? "" : "s"}</Typography>
                                {thread.unread && <Chip size="small" colorScheme="greenLighter">unread</Chip>}
                                {waiting !== null && <Chip size="small" colorScheme={waiting >= 7 ? "redLighter" : "orangeLighter"}>waiting on us</Chip>}
                            </div>
                        </Card>
                    );
                })}
            </div>
            {limit && (threads?.length ?? 0) > limit && (
                <Typography variant="caption" color="secondary">{(threads?.length ?? 0) - limit} more in the Email tab.</Typography>
            )}

            {selected && (
                <div className="flex flex-col gap-3">
                    <Separator orientation="horizontal" />
                    <div className="flex items-center gap-2">
                        <Typography variant="subtitle2" className="grow truncate">{threadTitle(selected)}</Typography>
                        <Button size="small" variant="text" onClick={() => navigate(inboxPath({ thread: selected.id }))}>Open in the inbox</Button>
                    </div>
                    <EmailThreadView messages={messages} mailboxes={mailboxes} />
                    {replying ? (
                        <EmailComposer
                            compact
                            target={{
                                mode: "reply",
                                threadId: selected.id,
                                mailboxId: selected.mailboxId,
                                to: (selected.participants ?? []).filter(Boolean),
                                prospectId: selected.prospectId,
                                companyId: selected.companyId,
                                recipientName: name
                            }}
                            mailboxes={mailboxes}
                            onCancel={() => setReplying(false)}
                            onSent={async () => {
                                setReplying(false);
                                snackbar.open({ type: "success", message: "Sent" });
                                await Promise.all([load(), loadMessages(selected.id)]);
                            }}
                        />
                    ) : (
                        <div>
                            <Button size="small" variant="outlined" onClick={() => setReplying(true)}>
                                <LucideIconByName name="Reply" size={iconSize.smallest} /> Reply
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
